import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import 'mapbox-gl/dist/mapbox-gl.css';

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const KM_PER_DEG_LAT = 111;
const KM_PER_MILE = 1.60934;

/**
 * Convert a radius around (lat, lng) into a square bounding box.
 * Latitude is uniform (~111 km/deg). Longitude shrinks toward the poles by cos(lat).
 */
function bboxFromRadius(centerLat, centerLng, radiusKm) {
  const dLat = radiusKm / KM_PER_DEG_LAT;
  const cosLat = Math.cos((centerLat * Math.PI) / 180) || 1;
  const dLng = radiusKm / (KM_PER_DEG_LAT * cosLat);
  return {
    northLat: centerLat + dLat,
    southLat: centerLat - dLat,
    eastLng: centerLng + dLng,
    westLng: centerLng - dLng,
  };
}

/**
 * RegionMapPicker — three integrated tools for defining a region:
 *   1. Type a city name (Mapbox geocoder, types=place,region) — auto-populates name,
 *      country, center, and uses Mapbox's `bbox` if returned.
 *   2. Set a radius (miles or km) around the center — recomputes a square bounding box.
 *   3. Live map preview with the box rendered as a polygon overlay.
 *
 * Calls `onChange(value)` with `{ name, country, centerLat, centerLng,
 * northLat, southLat, eastLng, westLng }`. The parent retains the source-of-truth
 * value and feeds it back via `value` prop so map + form stay in sync.
 */
export default function RegionMapPicker({ value = {}, onChange, defaultRadiusKm = 10 }) {
  const mapContainerRef = useRef(null);
  const geocoderContainerRef = useRef(null);
  const mapRef = useRef(null);
  const geocoderRef = useRef(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const [unit, setUnit] = useState('km');
  const [distance, setDistance] = useState(defaultRadiusKm);

  // Initialize map + geocoder once. Robust against StrictMode double-mount: we guard with
  // refs AND wipe the geocoder container's children on teardown so a double-effect run
  // doesn't leave a duplicate input behind.
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    if (!TOKEN) return;
    mapboxgl.accessToken = TOKEN;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: value.centerLng != null ? [value.centerLng, value.centerLat] : [-98.5, 39.5],
      zoom: value.centerLng != null ? 9 : 3,
    });
    mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    if (geocoderContainerRef.current) {
      // Clear any stale DOM left from a prior StrictMode mount.
      while (geocoderContainerRef.current.firstChild) {
        geocoderContainerRef.current.removeChild(geocoderContainerRef.current.firstChild);
      }

      const geocoder = new MapboxGeocoder({
        accessToken: TOKEN,
        types: 'place,locality,region,district',
        placeholder: 'Search for a city — e.g. Vancouver, Brooklyn',
        limit: 5,
      });
      geocoderRef.current = geocoder;
      geocoder.addTo(geocoderContainerRef.current);

      geocoder.on('result', (e) => {
        const r = e.result;
        const center = r.center; // [lng, lat]
        const country = r.context?.find((c) => c.id?.startsWith('country'))?.short_code?.toUpperCase().slice(0, 2)
          || r.short_code?.toUpperCase().slice(0, 2)
          || null;
        let bbox = r.bbox; // [west, south, east, north] when present

        if (!bbox) {
          // Fall back to the radius input if Mapbox didn't return a bbox (rare for cities).
          const km = unit === 'mi' ? distance * KM_PER_MILE : distance;
          const b = bboxFromRadius(center[1], center[0], km);
          bbox = [b.westLng, b.southLat, b.eastLng, b.northLat];
        }

        onChange?.({
          ...valueRef.current,
          name: r.place_name || r.text,
          country,
          centerLat: center[1],
          centerLng: center[0],
          westLng: bbox[0],
          southLat: bbox[1],
          eastLng: bbox[2],
          northLat: bbox[3],
        });
      });
    }

    return () => {
      // Tear down the geocoder DOM so a remount doesn't end up with two inputs.
      try { geocoderRef.current?.onRemove?.(); } catch { /* ignore */ }
      if (geocoderContainerRef.current) {
        while (geocoderContainerRef.current.firstChild) {
          geocoderContainerRef.current.removeChild(geocoderContainerRef.current.firstChild);
        }
      }
      try { mapRef.current?.remove(); } catch { /* ignore */ }
      mapRef.current = null;
      geocoderRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-render the bounding-box polygon every time `value` changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (value.northLat == null || value.southLat == null || value.eastLng == null || value.westLng == null) return;

    const apply = () => {
      const coords = [
        [value.westLng, value.northLat],
        [value.eastLng, value.northLat],
        [value.eastLng, value.southLat],
        [value.westLng, value.southLat],
        [value.westLng, value.northLat],
      ];
      const geo = { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] } };
      const src = map.getSource('region-bbox');
      if (src) {
        src.setData(geo);
      } else {
        map.addSource('region-bbox', { type: 'geojson', data: geo });
        map.addLayer({
          id: 'region-fill',
          type: 'fill',
          source: 'region-bbox',
          paint: { 'fill-color': '#000000', 'fill-opacity': 0.12 },
        });
        map.addLayer({
          id: 'region-outline',
          type: 'line',
          source: 'region-bbox',
          paint: { 'line-color': '#000000', 'line-width': 2 },
        });
      }

      // Center marker
      const centerSrcId = 'region-center';
      const center = { type: 'Feature', geometry: { type: 'Point', coordinates: [value.centerLng, value.centerLat] } };
      const csrc = map.getSource(centerSrcId);
      if (csrc) {
        csrc.setData(center);
      } else {
        map.addSource(centerSrcId, { type: 'geojson', data: center });
        map.addLayer({
          id: 'region-center-dot',
          type: 'circle',
          source: centerSrcId,
          paint: { 'circle-color': '#000000', 'circle-radius': 5 },
        });
      }

      try {
        map.fitBounds(
          [[value.westLng, value.southLat], [value.eastLng, value.northLat]],
          { padding: 40, animate: false, maxZoom: 12 },
        );
      } catch { /* ignore fit errors on degenerate boxes */ }
    };
    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }, [value.northLat, value.southLat, value.eastLng, value.westLng, value.centerLat, value.centerLng]);

  // Recompute bbox from the radius input.
  const applyRadius = () => {
    if (value.centerLat == null || value.centerLng == null) return;
    const km = unit === 'mi' ? distance * KM_PER_MILE : distance;
    const b = bboxFromRadius(value.centerLat, value.centerLng, km);
    onChange?.({ ...value, ...b });
  };

  if (!TOKEN) {
    return (
      <div className="border-2 border-red-600 p-3 font-mono text-xs text-red-700">
        REACT_APP_MAPBOX_TOKEN is not set. Map picker is unavailable.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-wider text-gray-600 mb-1">
          Search for a city
        </label>
        <div ref={geocoderContainerRef} className="region-geocoder" />
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-wider text-gray-600 mb-1">Radius</span>
          <input
            type="number"
            min="0"
            step="0.1"
            value={distance}
            onChange={(e) => setDistance(parseFloat(e.target.value) || 0)}
            className="w-28 border-2 border-black px-3 py-2 font-mono text-sm"
          />
        </label>
        <div className="flex border-2 border-black">
          <button
            type="button"
            onClick={() => setUnit('km')}
            className={`px-3 py-2 font-mono text-xs uppercase tracking-wider ${unit === 'km' ? 'bg-black text-white' : 'bg-white'}`}
          >
            KM
          </button>
          <button
            type="button"
            onClick={() => setUnit('mi')}
            className={`px-3 py-2 font-mono text-xs uppercase tracking-wider border-l-2 border-black ${unit === 'mi' ? 'bg-black text-white' : 'bg-white'}`}
          >
            MI
          </button>
        </div>
        <button
          type="button"
          onClick={applyRadius}
          disabled={value.centerLat == null}
          className="px-4 py-2 border-2 border-black font-mono text-xs font-bold uppercase tracking-wider bg-black text-white disabled:opacity-50"
        >
          APPLY RADIUS
        </button>
      </div>

      <div ref={mapContainerRef} className="h-72 border-2 border-black" />

      {value.northLat != null && value.centerLat != null && (
        <p className="font-mono text-[10px] text-gray-600">
          Box: N {Number(value.northLat).toFixed(4)} · S {Number(value.southLat).toFixed(4)} ·
          E {Number(value.eastLng).toFixed(4)} · W {Number(value.westLng).toFixed(4)} · Center{' '}
          {Number(value.centerLat).toFixed(4)}, {Number(value.centerLng).toFixed(4)}
        </p>
      )}

      <style>{`
        .region-geocoder .mapboxgl-ctrl-geocoder {
          width: 100%;
          max-width: none;
          box-shadow: none;
          border: 2px solid #000;
          border-radius: 0;
          font-family: ui-monospace, "SF Mono", Menlo, monospace;
        }
        .region-geocoder .mapboxgl-ctrl-geocoder--input {
          height: 42px;
          padding: 6px 30px 6px 36px;
          font-size: 14px;
        }
      `}</style>
    </div>
  );
}
